import { Card, IconButton, Icon, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Switch, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import DataTable from "../../../../Components/Table/DataTable";
import WDBox from "../../../../MaterialComponents/WDBox";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import AppService from "../../../../Configurations/Appservice";
import moment from "moment";
import { CUSTOMER_ORG_TYPE } from "../../../../Configurations/AppConfig";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useNavigate } from "react-router-dom";
import MockDataIndicator from "../../../../Components/Common/MockDataIndicator";

const tableInitialData = {
  columns: [
    { Header: "name", accessor: "name", width: "20%" },
    { Header: "contact person", accessor: "contactPerson", width: "25%" },
    { Header: "contact number", accessor: "contactNumber", width: "25%" },
    { Header: "address", accessor: "address" },
    { Header: "email", accessor: "email", width: "7%" },
    { Header: "created date", accessor: "createdAt" },
    { Header: "status", accessor: "status", width: "10%" },
    { Header: "edit", accessor: "edit" },
  ],

  rows: [],
};

const PartnerOrganisations = () => {
  const [tableData, setTableData] = useState(tableInitialData);
  const [isLoading, loading] = useState(true);
  const [isMockData, setIsMockData] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateScreenTitle("Partner Organisations"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  // Check if current user is admin
  const isAdmin = () => {
    try {
      const authInfo = JSON.parse(localStorage.getItem("authInfo"));
      return authInfo && authInfo.roles && authInfo.roles.includes("ROOT_ADMIN");
    } catch {
      return false;
    }
  };

  // Dialog state for status change
  const [statusDialog, setStatusDialog] = useState({ open: false, orgId: null, newStatus: false });

  // Get user details from principalInfo
  let userDetails = { name: '', userType: '', roles: [] };
  try {
    const principalInfoRaw = JSON.parse(localStorage.getItem("principalInfo"));
    const principalInfo = principalInfoRaw.principalInfo || principalInfoRaw;
    userDetails = {
      name: principalInfo.name,
      userType: principalInfo.userType,
      roles: principalInfo.roles,
    };
  } catch {}

  const handleStatusChange = async (orgId, newStatus) => {
    try {
      await AppService.put(
        `v1/organisation/active?organisationId=${orgId}&isActive=${newStatus}`
      );
      // Update UI
      setTableData((prev) => {
        const updatedRows = prev.rows.map((row) =>
          row.id === orgId ? { ...row, status: newStatus ? "Active" : "Inactive" } : row
        );
        return { ...prev, rows: updatedRows };
      });
    } catch (err) {
      // Error handled globally
    }
  };


  const _generateTableRows = (response) => {
    const rows = response.map((item) => ({
      id: item.id,
      name: item.name,
      contactPerson: item.contactPerson,
      address: item.address,
      email: item.email,
      createdAt: moment.utc(item.createdAt).local().format("YYYY-MM-DD"),
      contactNumber: item.contactNumber,
      status: item.active ? "Active" : "Inactive",
      edit: (
        <IconButton size="small" onClick={() => _navigateToEditScreen(item.id)}>
          <Icon>edit</Icon>
        </IconButton>
      ),
      status: (
        <>
          <span>{item.active ? "Active" : "Inactive"}</span>
          <Switch
            checked={item.active}
            color={item.active ? "success" : "error"}
            onChange={() => {
              console.log("Status switch toggled for org:", item.id, "newStatus:", !item.active);
              setStatusDialog({ open: true, orgId: item.id, newStatus: !item.active });
            }}
            style={{ marginLeft: 8 }}
          />
        </>
      ),
    }));

    let tableDataDuplicate = { ...tableData };
    tableDataDuplicate.rows = rows;
    setTableData(tableDataDuplicate);
  };


  useEffect(() => {
    const params = {
      type: "PARTNER",
    };
    AppService.get("v1/organisation", { params })
      .then((response) => {
        _generateTableRows(response);
        setIsMockData(false);
      })
      .catch((error) => {
        console.error('Failed to fetch partner organisations, using mock data', error);
        // Use mock data as fallback
        const mockData = [
          {
            id: 'mock-partner-1',
            name: 'Emirates Tech Solutions',
            contactPerson: 'Ahmed Al Mansouri',
            contactNumber: '+971 4 555 1234',
            address: 'Dubai Internet City, Dubai',
            email: 'ahmed@emiratestech.ae',
            createdAt: new Date(Date.now() - 90 * 86400000).toISOString(),
            active: true
          },
          {
            id: 'mock-partner-2',
            name: 'Gulf Digital Services',
            contactPerson: 'Fatima Hassan',
            contactNumber: '+971 2 555 5678',
            address: 'Masdar City, Abu Dhabi',
            email: 'fatima@gulfdigital.ae',
            createdAt: new Date(Date.now() - 60 * 86400000).toISOString(),
            active: true
          },
          {
            id: 'mock-partner-3',
            name: 'Sharjah IT Partners',
            contactPerson: 'Mohammed Al Qasimi',
            contactNumber: '+971 6 555 9012',
            address: 'Sharjah Research Technology Park',
            email: 'mohammed@sharjahit.ae',
            createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
            active: false
          }
        ];
        _generateTableRows(mockData);
        setIsMockData(true);
      })
      .finally(() => {
        loading(false);
      });
  }, []);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-Partner/adm-new-partner-organisation");
  };

  const _navigateToEditScreen = (organisationId) => {
    navigate("/ADM-Customer/adm-edit-customer-organisation", {
      state: {
        organisationId,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMockData && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Partner Organisations
          </Typography>
          <MockDataIndicator isMock={isMockData} position="chip" size="small" />
        </Box>
      )}
      {/* <Card sx={{ mb: 1, p: 2 }}>{_renderSearchBar()}</Card> */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ ...statusDialog, open: false })}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to set status to <b>{statusDialog.newStatus ? "Active" : "Inactive"}</b> for this organisation?
            <br/><br/>
            <b>User Details:</b><br/>
            Name: {userDetails.name}<br/>
            User Type: {userDetails.userType}<br/>
            Roles: {Array.isArray(userDetails.roles) ? userDetails.roles.join(", ") : userDetails.roles}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ ...statusDialog, open: false })} color="inherit">Cancel</Button>
          <Button onClick={() => {
            handleStatusChange(statusDialog.orgId, statusDialog.newStatus);
            setStatusDialog({ ...statusDialog, open: false });
          }} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {
        <DataTable
          table={tableData}
          showAddButton={true}
          addButtonOnClick={_navigateToCreationScreen}
        />
      }
    </Box>
  );
};

export default PartnerOrganisations;
